import React, { useState, useEffect, useContext } from "react";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import OwnerLayout from "../../../layout/OwnerLayout";
import { UserContext } from "../../../../context/UserContext"; // Import User Context

import { baseURL } from "../../../../api/api";
import {
  addRestaurants,
  getRestaurantById,
  updateRestaurants,
} from "../../../../api/restaurants.api";
import { getActiveMobileLocations } from "../../../../api/vehicle.api";
import { errorMsg, success } from "../../../../utils/notifications";
import { getCurrentUser } from "../../../../api/user.api";

const cuisineTypes = [
  "Fine Dining",
  "Casual Dining",
  "Fast Food",
  "Cafe",
  "Other",
];

const initialData = {
  name: "",
  location: "",
  cuisineType: "Other",
  seatingCapacity: "",
  basePricePerWeek: "",
  openingHours: {
    monday: { start: "", end: "" },
    tuesday: { start: "", end: "" },
    wednesday: { start: "", end: "" },
    thursday: { start: "", end: "" },
    friday: { start: "", end: "" },
    saturday: { start: "", end: "" },
    sunday: { start: "", end: "" },
  },
  footTrafficEstimate: "",
  description: "",
  contactInformation: {
    phone: "",
    email: "",
  },
  images: [], // New field for storing image URLs or files
  owner: "",
};

function AddRestaurants() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [addRestaurantData, setAddRestaurantData] = useState(initialData);
  const [saveRestaurantLoading, setSaveRestaurantLoading] = useState(false);
  const [currentOwner, setCurrentOwner] = useState(null);
  const [locationTypes, setlocationTypes] = useState([]);
  const serviceLocationOptions = locationTypes.map((location) => ({
    value: location._id, // This will be sent to the backend
    label: location.name, // This will be displayed in the dropdown
  }));
  const { user } = useContext(UserContext); // Get user from context
  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const response = await getCurrentUser();
        if (response.data.success) {
          setCurrentOwner(response.data.user.ownerId);
          console.log("Current owner:", response.data.user.ownerId);
        } else {
          console.error("Failed to fetch current user.");
        }
      } catch (error) {
        console.error("Error fetching current user:", error);
      }
    };
    fetchCurrentUser();
  }, []);

  // Fetch restaurant data or initialize form for a new restaurant
  useEffect(() => {
    const fetchRestaurantData = async () => {
      if (id) {
        try {
          const response = await getRestaurantById(id);
          setAddRestaurantData({
            ...response?.data,
            owner: response?.data.owner || currentOwner,
          });
        } catch (error) {
          console.error("Error fetching restaurant data:", error);
        }
      } else {
        setAddRestaurantData((prevData) => ({
          ...prevData,
          owner: currentOwner,
        }));
      }
      getActiveMobileLocations()
        .then((res) => {
          setlocationTypes(res?.data || []);
        })
        .catch((error) => {
          console.error("Error fetching vehicle types:", error);
        });
    };
    if (currentOwner !== null) {
      fetchRestaurantData();
    }
  }, [id, currentOwner]);

  // Handle input changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddRestaurantData((prevData) => ({ ...prevData, [name]: value }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files); // Get the files from the input
    console.log("Files:", files);

    // Ensure the total number of images is between 1 and 5
    if (files.length + addRestaurantData.images.length > 5) {
      errorMsg("You can only upload a maximum of 5 images.");
      return;
    }

    // Ensure the files are valid image files
    const validFiles = files.filter((file) => file.type.startsWith("image/"));
    if (validFiles.length === 0) {
      errorMsg("Please upload valid image files.");
      return;
    }

    // Update the restaurant data with file objects, not just file names
    setAddRestaurantData((prevData) => ({
      ...prevData,
      images: [...prevData.images, ...validFiles], // Store the files themselves here
    }));
  };

  const removeImage = (index, isExistingImage = false) => {
    if (isExistingImage) {
      // If it's an existing image, mark it as removed
      setAddRestaurantData((prevData) => ({
        ...prevData,
        removedImages: [
          ...(prevData.removedImages || []),
          prevData.images[index].url, // Assume 'url' is the property for existing images
        ],
        images: prevData.images.filter((_, i) => i !== index),
      }));
    } else {
      // If it's a newly uploaded image (file object), just remove it from the local state
      setAddRestaurantData((prevData) => ({
        ...prevData,
        images: prevData.images.filter((_, i) => i !== index),
      }));
    }
  };

  // Handle opening hours changes
  const handleOpeningHoursChange = (day, timeType, value) => {
    setAddRestaurantData((prevData) => ({
      ...prevData,
      openingHours: {
        ...prevData.openingHours,
        [day]: {
          ...prevData.openingHours[day],
          [timeType]: value,
        },
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveRestaurantLoading(true);
    console.log("Add restaurant body: ", addRestaurantData);
    if (!addRestaurantData.owner) {
      errorMsg("Owner information is missing. Please try again.");
      setSaveRestaurantLoading(false);
      return;
    }

    const formData = new FormData();

    // Append restaurant fields to FormData
    formData.append("name", addRestaurantData.name);
    const matchedLocation = serviceLocationOptions.find(
      (option) => option.label === addRestaurantData?.location
    );
    formData.append(
      "location",
      matchedLocation?.value || addRestaurantData?.location
    );

    formData.append("cuisineType", addRestaurantData.cuisineType);
    formData.append("seatingCapacity", addRestaurantData.seatingCapacity);
    formData.append("basePricePerWeek", addRestaurantData.basePricePerWeek);
    formData.append(
      "footTrafficEstimate",
      addRestaurantData.footTrafficEstimate
    );
    formData.append("description", addRestaurantData.description);
    formData.append(
      "contactInformation[phone]",
      addRestaurantData.contactInformation.phone
    );
    formData.append(
      "contactInformation[email]",
      addRestaurantData.contactInformation.email
    );
    formData.append("owner", addRestaurantData.owner);

    // Append opening hours to FormData
    Object.keys(addRestaurantData.openingHours).forEach((day) => {
      formData.append(
        `openingHours[${day}][start]`,
        addRestaurantData.openingHours[day].start
      );
      formData.append(
        `openingHours[${day}][end]`,
        addRestaurantData.openingHours[day].end
      );
    });

    const existingImages = addRestaurantData.images.filter(
      (image) => typeof image === "string" // URLs for existing images
    );
    const newImages = addRestaurantData.images.filter(
      (image) => image instanceof File // File objects for new images
    );

    // Append existing images as an array of URLs
    existingImages.forEach((image, index) => {
      formData.append(`existingImages[${index}]`, image);
    });

    // Append new images to FormData
    newImages.forEach((image) => {
      formData.append("images", image);
    });

    try {
      if (id) {
        await updateRestaurants(id, formData); // Ensure `updateRestaurants` can handle `FormData`
        success("Fixed Location updated successfully");
      } else {
        await addRestaurants(formData); // Ensure `addRestaurants` can handle `FormData`
        success("Fixed Location added successfully");
      }
      navigate("/fixedLocations");
    } catch (error) {
      errorMsg(error?.response?.data?.message || "Something went wrong!");
    } finally {
      setSaveRestaurantLoading(false);
    }
  };

  return (
    <OwnerLayout ownerDetails={user}>
      <div className="container mx-auto px-4">
        <h1 className="text-2xl font-semibold text-gray-700 mb-6">
          {id ? "Update Fixed Location" : "Add New Fixed Location"}
        </h1>
        <form
          onSubmit={handleSubmit}
          className="bg-white shadow rounded-lg p-8"
        >
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {/* Restaurant Name */}
            <div className="col-span-1">
              <label
                htmlFor="name"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Fixed Location Name
              </label>
              <input
                type="text"
                name="name"
                placeholder="Enter restaurant name"
                value={addRestaurantData.name}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                required
              />
            </div>

            {/* Location */}
            <div className="col-span-1">
              <label
                htmlFor="location"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Location
              </label>

              <Select
                name="location"
                options={serviceLocationOptions}
                value={serviceLocationOptions.find(
                  (option) => option.label === addRestaurantData?.location
                )}
                className="basic-multi-select"
                classNamePrefix="select location"
                onChange={(selectedOption) => {
                  setAddRestaurantData({
                    ...addRestaurantData,
                    location: selectedOption?.value,
                  });
                }}
                required
              />
            </div>

            {/* Cuisine Type */}
            <div className="col-span-1">
              <label
                htmlFor="cuisineType"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Cuisine Type
              </label>
              <Select
                name="cuisineType"
                id="cuisineType"
                options={cuisineTypes.map((type) => ({
                  value: type,
                  label: type,
                }))}
                value={{
                  value: addRestaurantData.cuisineType,
                  label: addRestaurantData.cuisineType,
                }}
                onChange={(selectedOption) =>
                  setAddRestaurantData({
                    ...addRestaurantData,
                    cuisineType: selectedOption?.value,
                  })
                }
                className="w-full"
                required
              />
            </div>

            {/* Seating Capacity */}
            <div className="col-span-1">
              <label
                htmlFor="seatingCapacity"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Seating Capacity
              </label>
              <input
                type="number"
                name="seatingCapacity"
                placeholder="Enter seating capacity"
                value={addRestaurantData.seatingCapacity}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                required
              />
            </div>

            {/* Foot Traffic Estimate */}
            <div className="col-span-1">
              <label
                htmlFor="footTrafficEstimate"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Foot Traffic Estimate
              </label>
              <input
                type="number"
                placeholder="Enter foot traffic estimate"
                name="footTrafficEstimate"
                value={addRestaurantData.footTrafficEstimate}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>

            {/* Contact Information - Phone */}
            <div className="col-span-1">
              <label
                htmlFor="contactPhone"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={addRestaurantData.contactInformation.phone}
                onChange={(e) => {
                  let newValue = e.target.value;

                  // Allow only digits and "+" at start
                  newValue = newValue.replace(/[^\d+]/g, "");

                  // Ensure "+" only at the start
                  if (newValue.includes("+") && newValue.indexOf("+") !== 0) {
                    newValue = newValue.replace(/\+/g, "");
                  }

                  // If more than one "+", keep only the first
                  if ((newValue.match(/\+/g) || []).length > 1) {
                    newValue = "+" + newValue.replace(/\+/g, "");
                  }

                  setAddRestaurantData({
                    ...addRestaurantData,
                    contactInformation: {
                      ...addRestaurantData.contactInformation,
                      phone: newValue,
                    },
                  });
                }}
                className="w-full border rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="Enter phone number"
                required
              />
            </div>

            {/* Contact Information - Email */}
            <div className="col-span-1">
              <label
                htmlFor="contactEmail"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Email
              </label>
              <input
                type="email"
                name="email"
                value={addRestaurantData.contactInformation.email}
                onChange={(e) =>
                  setAddRestaurantData({
                    ...addRestaurantData,
                    contactInformation: {
                      ...addRestaurantData.contactInformation,
                      email: e.target.value,
                    },
                  })
                }
                className="w-full border rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="Enter email address"
                required
              />
            </div>
            <div className="col-span-1">
              <label
                htmlFor="basePricePerWeek"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Fixed Location Price Per Week{" "}
              </label>
              <input
                type="number"
                name="basePricePerWeek"
                value={addRestaurantData.basePricePerWeek}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                required
              />
            </div>
            {/* Description */}
            <div className="col-span-2">
              <label
                htmlFor="description"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Description
              </label>
              <textarea
                name="description"
                placeholder="Enter some Information about your Restaurant"
                value={addRestaurantData.description}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                rows="1"
              />
            </div>
          </div>
          <div className="col-span-3">
            <label className="block text-sm font-semibold text-gray-600 mb-2">
              Upload Images (Max: 5)
            </label>

            {/* Custom file input */}
            <div className="relative flex justify-center items-center w-full h-32 border rounded-md cursor-pointer bg-gray-100">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
              <span className="text-4xl text-gray-500">+</span>
            </div>

            {/* Show selected images */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {addRestaurantData.images.map((image, index) => (
                <div key={index} className="relative group">
                  {image && (
                    <img
                      // Check if the image is a URL or File object
                      src={
                        typeof image === "string"
                          ? `${baseURL}/s3/images/${image}` // Construct the full S3 URL for each image
                          : URL.createObjectURL(image) // Use object URL for File objects (newly uploaded images)
                      }
                      alt={`Uploaded ${index + 1}`}
                      className="w-full h-32 object-cover rounded-md"
                    />
                  )}
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-75 group-hover:opacity-100"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Opening Hours */}
          <div className="mt-6">
            <label className="block text-sm font-semibold text-gray-600 mb-4">
              Opening Hours
            </label>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-7">
              {Object.keys(addRestaurantData.openingHours).map((day) => (
                <div key={day} className="flex flex-col items-center">
                  <span className="font-medium text-gray-700 capitalize">
                    {day}
                  </span>
                  <input
                    type="time"
                    value={addRestaurantData.openingHours[day].start}
                    onChange={(e) =>
                      handleOpeningHoursChange(day, "start", e.target.value)
                    }
                    className="w-full border rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                  <input
                    type="time"
                    value={addRestaurantData.openingHours[day].end}
                    onChange={(e) =>
                      handleOpeningHoursChange(day, "end", e.target.value)
                    }
                    className="w-full border rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 mt-2"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-8">
            <button
              type="submit"
              className={`w-full sm:w-auto px-4 py-2 bg-yellow text-black rounded-md font-semibold hover:scale-105 ${
                saveRestaurantLoading && "opacity-100"
              }`}
              disabled={saveRestaurantLoading}
            >
              {saveRestaurantLoading
                ? "Saving..."
                : id
                ? "Update Fixed Location "
                : "Save Fixed Location"}
            </button>
          </div>
        </form>
      </div>
    </OwnerLayout>
  );
}

export default AddRestaurants;
